import classNames from 'classnames'
import type { InputHTMLAttributes } from 'react'
import { forwardRef } from 'react'

import type { LabelProps } from '../text-input/label'
import { Label } from '../text-input/label'
import { PRESETS } from '../text-input/presets'

/**
 * Displays a styled select input
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
    (
        {
            preset = 'rounded',
            adornment,
            label,
            valid,
            error,
            size,
            validationCount,
            className,
            'data-testid': testId,
            onFocus,
            name,
            options,
            fullWidth,
            ...restOfProps
        },
        ref,
    ) => {
        const styles = PRESETS[preset]

        return (
            <Label
                size={size}
                preset={preset}
                adornment={adornment}
                validationCount={validationCount}
                valid={valid}
                error={error}
                label={label}
                data-testid={testId}
                fullWidth={fullWidth}
                className={className}
            >
                <select
                    aria-invalid={!valid}
                    ref={ref}
                    name={name}
                    onFocus={onFocus}
                    className={classNames(
                        styles.input,
                        styles[`input-${size}` as keyof typeof styles],
                        {
                            [styles['input-with-adornment']]: adornment,
                            [styles['input-with-error']]: !valid,
                        },
                    )}
                    {...restOfProps}
                >
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </Label>
        )
    },
)

Select.displayName = 'Select'

/** Core input for all text-based inputs */
export type SelectProps = LabelProps &
    Pick<
        InputHTMLAttributes<HTMLSelectElement>,
        | 'alt'
        | 'autoFocus'
        | 'disabled'
        | 'inputMode'
        | 'maxLength'
        | 'minLength'
        | 'name'
        | 'onBlur'
        | 'onChange'
        | 'onChangeCapture'
        | 'onFocus'
        | 'pattern'
        | 'placeholder'
        | 'readOnly'
        | 'required'
        | 'data-testid'
    > & {
        /**
         * Value for controlled version.
         */
        value?: string

        /**
         * Default value for uncontrolled version.
         */
        defaultValue?: string

        /**
         * Array of options for select
         */
        options: Array<{
            value: string
            label: string
        }>
    }
